/**
 * AttachmentsSection — widget tabeli/kafelków załączników kartoteki (`record_attachments`).
 *
 * Załącznik jest polimorficzny — podpięty pod `ownerType` + `ownerId` (kontrahent,
 * pracownik, towar, user, dokument). Plik fizycznie w MinIO; front NIGDY nie zna
 * `fileKey` ani publicznego URL-a — treść/podgląd/pobranie idzie wyłącznie przez
 * strumieniujący endpoint `.../:id/content` (autoryzowany, taki sam mechanizm jak
 * `apps/admin/src/components/modules/RecordAttachments.tsx` dla załączników modułów
 * custom). Usuwanie jest soft-delete (miękkie) — brak przycisku hard-delete w tym
 * widgecie (endpoint wspiera `?hard=true`, ale to świadomie poza zakresem UI).
 *
 * Backend (kontrakt):
 *  - GET    /api/attachments?ownerType&ownerId        → { attachments: RecordAttachmentDTO[] }
 *  - POST   /api/attachments (multipart)              → { attachment }
 *  - PATCH  /api/attachments/:id (description/isPrimary) → { attachment }
 *  - DELETE /api/attachments/:id (soft; ?hard=true opcjonalnie)
 *  - GET    /api/attachments/:id/content              → strumień pliku (blob)
 *
 * `companyId` (opcjonalny) — jawny nagłówek `X-Company-Id` per-request, analogicznie
 * do wzorca w *Detail.tsx (np. ContractDetail) dla encji company-scoped w trybie
 * "Wszystkie firmy"; gdy pominięty, interceptor `lib/api.ts` dopina aktywną firmę
 * z localStorage (o ile jest ustawiona).
 */

import { type DragEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttachmentOwnerType, RecordAttachmentDTO } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type AxiosRequestConfig, isAxiosError } from 'axios';
import {
  AlertCircle,
  Check,
  Download,
  Eye,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  LayoutGrid,
  List,
  Music,
  Paperclip,
  Pencil,
  Star,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { DocumentPreviewPanel } from './DocumentPreviewPanel';
import { formatFileSize, getFileExtension, getPreviewKind, isEmbeddablePreviewKind } from './utils';

export interface AttachmentsSectionProps {
  ownerType: AttachmentOwnerType;
  ownerId: string;
  companyId?: string;
  /**
   * Przypięty element nad strefą uploadu — „wirtualny" załącznik spoza MinIO (np. oficjalny
   * PDF faktury KSeF generowany w locie). Liczy się do licznika i chowa pusty stan.
   */
  pinnedItem?: ReactNode;
}

type ViewMode = 'list' | 'grid';

interface UpdateAttachmentPatch {
  description?: string;
  isPrimary?: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Buduje config axios z opcjonalnym jawnym nagłówkiem X-Company-Id (per §4.8 wzorzec create-time). */
function buildRequestConfig(
  companyId: string | undefined,
  options: {
    params?: Record<string, string>;
    responseType?: 'blob';
    multipart?: boolean;
  } = {},
): AxiosRequestConfig {
  const headers: Record<string, string> = {};
  if (companyId) headers['X-Company-Id'] = companyId;
  if (options.multipart) headers['Content-Type'] = 'multipart/form-data';

  const config: AxiosRequestConfig = {};
  if (Object.keys(headers).length > 0) config.headers = headers;
  if (options.params) config.params = options.params;
  if (options.responseType) config.responseType = options.responseType;
  return config;
}

/**
 * Czy załącznik jest podglądalny inline — deleguje do `getPreviewKind`/
 * `isEmbeddablePreviewKind` z `./utils` (te same reguły co `DocumentPreviewPanel`,
 * czyli image/pdf/xml/text), zamiast osobnej listy image/pdf sprzed reconciliacji
 * z tamtym komponentem. Reszta (office/video/audio/nieznane) → od razu pobranie.
 */
function canPreviewInline(att: RecordAttachmentDTO): boolean {
  return isEmbeddablePreviewKind(getPreviewKind(att.mimeType, getFileExtension(att.fileName)));
}

function pickFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('audio/')) return Music;
  if (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('text/') ||
    mimeType.includes('document') ||
    mimeType.includes('spreadsheet')
  ) {
    return FileText;
  }
  return FileIcon;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function extractErrorMessage(err: unknown): string | undefined {
  if (isAxiosError(err)) {
    return (err.response?.data as { error?: string } | undefined)?.error;
  }
  return undefined;
}

// ─── Miniatura (obraz pobrany jako autoryzowany blob, bez publicznego URL-a) ──

interface AttachmentThumbnailProps {
  attachment: RecordAttachmentDTO;
  companyId?: string;
  onClick?: () => void;
  className: string;
}

function AttachmentThumbnail({
  attachment,
  companyId,
  onClick,
  className,
}: AttachmentThumbnailProps) {
  const isImage = attachment.mimeType.startsWith('image/');

  const { data: blob } = useQuery({
    queryKey: ['attachment-content', attachment.id],
    queryFn: async () => {
      const res = await api.get<Blob>(
        `/attachments/${attachment.id}/content`,
        buildRequestConfig(companyId, { responseType: 'blob' }),
      );
      return res.data;
    },
    enabled: isImage,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });

  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  if (isImage && url) {
    return <img src={url} alt="" onClick={onClick} className={className} />;
  }

  const Icon = pickFileIcon(attachment.mimeType);
  return (
    <div
      onClick={onClick}
      className={`${className} flex items-center justify-center bg-bg-tertiary`}
    >
      <Icon className="h-5 w-5 text-text-muted" aria-hidden="true" />
    </div>
  );
}

// ─── Główny komponent ───────────────────────────────────────────────────────

export function AttachmentsSection({
  ownerType,
  ownerId,
  companyId,
  pinnedItem,
}: AttachmentsSectionProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [dragOver, setDragOver] = useState(false);
  const [uploadingNames, setUploadingNames] = useState<string[]>([]);
  const [pendingDelete, setPendingDelete] = useState<RecordAttachmentDTO | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [previewTarget, setPreviewTarget] = useState<RecordAttachmentDTO | null>(null);

  const queryKey = ['attachments', ownerType, ownerId];

  const listQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get<{ attachments: RecordAttachmentDTO[] }>(
        '/attachments',
        buildRequestConfig(companyId, { params: { ownerType, ownerId } }),
      );
      return res.data.attachments;
    },
  });

  const attachments = listQuery.data ?? [];

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('ownerType', ownerType);
      formData.append('ownerId', ownerId);
      formData.append('file', file);
      const res = await api.post<{ attachment: RecordAttachmentDTO }>(
        '/attachments',
        formData,
        buildRequestConfig(companyId, { multipart: true }),
      );
      return res.data.attachment;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateAttachmentPatch }) => {
      const res = await api.patch<{ attachment: RecordAttachmentDTO }>(
        `/attachments/${id}`,
        patch,
        buildRequestConfig(companyId),
      );
      return res.data.attachment;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('attachments.toasts.updateError') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/attachments/${id}`, buildRequestConfig(companyId));
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('attachments.toasts.deleted') });
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('attachments.toasts.deleteError') });
    },
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    // Sekwencyjnie (nie Promise.all) — unika wyścigu o `is_primary` (partial unique
    // per owner) i po prostu jest łatwiejsze do śledzenia w UI (jeden spinner na raz).
    for (const file of Array.from(files)) {
      setUploadingNames((prev) => [...prev, file.name]);
      try {
        await uploadMutation.mutateAsync(file);
        toast.show({
          variant: 'success',
          title: t('attachments.toasts.uploaded', { fileName: file.name }),
        });
        void queryClient.invalidateQueries({ queryKey });
      } catch (err) {
        toast.show({
          variant: 'error',
          title: t('attachments.toasts.uploadError', { fileName: file.name }),
          description: extractErrorMessage(err),
        });
      } finally {
        setUploadingNames((prev) => {
          const idx = prev.indexOf(file.name);
          if (idx === -1) return prev;
          const next = [...prev];
          next.splice(idx, 1);
          return next;
        });
      }
    }
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    void handleFiles(e.dataTransfer.files);
  }

  function startEditDescription(att: RecordAttachmentDTO) {
    setEditingId(att.id);
    setEditingValue(att.description ?? '');
    setTimeout(() => editInputRef.current?.focus(), 30);
  }

  function saveDescription(id: string) {
    updateMutation.mutate(
      { id, patch: { description: editingValue } },
      { onSuccess: () => setEditingId(null) },
    );
  }

  function togglePrimary(att: RecordAttachmentDTO) {
    updateMutation.mutate({ id: att.id, patch: { isPrimary: !att.isPrimary } });
  }

  async function handleDownload(att: RecordAttachmentDTO) {
    try {
      const res = await api.get<Blob>(
        `/attachments/${att.id}/content`,
        buildRequestConfig(companyId, { responseType: 'blob' }),
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      toast.show({ variant: 'error', title: t('attachments.toasts.downloadError') });
    }
  }

  // Podgląd deleguje pobranie/blob-URL do `DocumentPreviewPanel` (`useAttachmentBlobUrl`
  // wewnątrz) — tu tylko wybieramy AKTYWNY załącznik (kontrolowany komponent, patrz
  // `activeAttachmentId`/`onActiveAttachmentChange` w renderze Modala poniżej).
  function openPreview(att: RecordAttachmentDTO) {
    if (!canPreviewInline(att)) {
      void handleDownload(att);
      return;
    }
    setPreviewTarget(att);
  }

  function closePreview() {
    setPreviewTarget(null);
  }

  function handleConfirmDelete() {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
  }

  function renderDescriptionEditor(att: RecordAttachmentDTO, compact: boolean) {
    if (editingId === att.id) {
      return (
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <input
            ref={editInputRef}
            type="text"
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            // Opis załącznika to edycja w miejscu: zapisuje się Enterem, a Esc w polu go anuluje.
            // Niezatwierdzony opis nie jest „niezapisaną zmianą" formularza, w którym sekcja jest
            // osadzona (zabezpieczenie zamknięcia okna) — jak Esc w polu, przepada bez pytania.
            data-no-dirty
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveDescription(att.id);
              if (e.key === 'Escape') {
                // Esc anuluje edycję opisu — „obsłużone", okno nadrzędne się nie zamyka.
                e.preventDefault();
                setEditingId(null);
              }
            }}
            placeholder={t('attachments.descriptionPlaceholder')}
            disabled={updateMutation.isPending}
            className={`min-w-0 flex-1 rounded border border-accent-400 bg-bg-primary px-2 py-0.5 text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-400 ${compact ? 'text-[10px]' : 'text-xs'}`}
          />
          <button
            type="button"
            onClick={() => saveDescription(att.id)}
            disabled={updateMutation.isPending}
            className="text-accent-600 hover:text-accent-700"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setEditingId(null)}
            className="text-text-muted hover:text-text-primary"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={() => startEditDescription(att)}
        className={`inline-flex min-w-0 items-center gap-1 truncate text-left text-text-tertiary hover:text-accent-600 ${compact ? 'text-[10px]' : 'text-xs'}`}
      >
        <span className="truncate">{att.description || t('attachments.noDescription')}</span>
        <Pencil className="h-3 w-3 flex-shrink-0" />
      </button>
    );
  }

  function renderRow(att: RecordAttachmentDTO) {
    const canPreview = canPreviewInline(att);
    return (
      <li
        key={att.id}
        data-testid={`attachment-row-${att.id}`}
        className={[
          'flex items-start gap-3 rounded-lg border px-3 py-2.5',
          att.isPrimary
            ? 'border-amber-400/60 bg-amber-500/5'
            : 'border-border-primary bg-bg-primary',
        ].join(' ')}
      >
        <AttachmentThumbnail
          attachment={att}
          companyId={companyId}
          onClick={() => (canPreview ? openPreview(att) : undefined)}
          className="h-10 w-10 flex-shrink-0 cursor-pointer rounded border border-border-secondary object-cover"
        />
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => (canPreview ? openPreview(att) : handleDownload(att))}
            title={att.fileName}
            data-testid={`attachment-row-${att.id}-name`}
            className="block w-full truncate text-left text-sm font-medium text-text-primary hover:text-accent-600"
          >
            {att.fileName}
          </button>
          <p className="text-xs text-text-tertiary">
            {formatFileSize(att.sizeBytes)} · {formatDate(att.createdAt)}
          </p>
          <div className="mt-1">{renderDescriptionEditor(att, false)}</div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => togglePrimary(att)}
            disabled={updateMutation.isPending}
            title={
              att.isPrimary
                ? t('attachments.actions.unsetPrimary')
                : t('attachments.actions.setPrimary')
            }
            aria-label={
              att.isPrimary
                ? t('attachments.actions.unsetPrimary')
                : t('attachments.actions.setPrimary')
            }
            className={`rounded p-1.5 transition-colors ${
              att.isPrimary ? 'text-amber-500' : 'text-text-muted hover:text-amber-500'
            }`}
          >
            <Star className={`h-4 w-4 ${att.isPrimary ? 'fill-current' : ''}`} />
          </button>
          {canPreview && (
            <button
              type="button"
              onClick={() => openPreview(att)}
              title={t('attachments.actions.preview')}
              aria-label={t('attachments.actions.preview')}
              className="rounded p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => handleDownload(att)}
            title={t('attachments.actions.download')}
            aria-label={t('attachments.actions.download')}
            className="rounded p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(att)}
            title={t('attachments.actions.delete')}
            aria-label={t('attachments.actions.delete')}
            className="rounded p-1.5 text-text-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </li>
    );
  }

  function renderTile(att: RecordAttachmentDTO) {
    const canPreview = canPreviewInline(att);
    return (
      <div
        key={att.id}
        data-testid={`attachment-tile-${att.id}`}
        className={[
          'group overflow-hidden rounded-lg border transition-shadow hover:shadow-md',
          att.isPrimary ? 'border-amber-400/60 ring-1 ring-amber-300/40' : 'border-border-primary',
        ].join(' ')}
      >
        <div className="relative h-28 w-full">
          <AttachmentThumbnail
            attachment={att}
            companyId={companyId}
            onClick={() => (canPreview ? openPreview(att) : handleDownload(att))}
            className="h-full w-full cursor-pointer object-cover"
          />
          {att.isPrimary && (
            <Star className="absolute left-1.5 top-1.5 h-4 w-4 fill-current text-amber-500 drop-shadow" />
          )}
          <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/0 opacity-0 transition-colors group-hover:bg-black/30 group-hover:opacity-100">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                togglePrimary(att);
              }}
              title={
                att.isPrimary
                  ? t('attachments.actions.unsetPrimary')
                  : t('attachments.actions.setPrimary')
              }
              aria-label={
                att.isPrimary
                  ? t('attachments.actions.unsetPrimary')
                  : t('attachments.actions.setPrimary')
              }
              className="rounded-full bg-white/90 p-1.5 text-text-primary shadow hover:bg-white dark:bg-gray-800/90"
            >
              <Star className={`h-4 w-4 ${att.isPrimary ? 'fill-current text-amber-500' : ''}`} />
            </button>
            {canPreview && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openPreview(att);
                }}
                title={t('attachments.actions.preview')}
                aria-label={t('attachments.actions.preview')}
                className="rounded-full bg-white/90 p-1.5 text-text-primary shadow hover:bg-white dark:bg-gray-800/90"
              >
                <Eye className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void handleDownload(att);
              }}
              title={t('attachments.actions.download')}
              aria-label={t('attachments.actions.download')}
              className="rounded-full bg-white/90 p-1.5 text-text-primary shadow hover:bg-white dark:bg-gray-800/90"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPendingDelete(att);
              }}
              title={t('attachments.actions.delete')}
              aria-label={t('attachments.actions.delete')}
              className="rounded-full bg-white/90 p-1.5 text-red-600 shadow hover:bg-white dark:bg-gray-800/90"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="p-2">
          <p className="truncate text-xs font-medium text-text-primary" title={att.fileName}>
            {att.fileName}
          </p>
          <p className="mt-0.5 text-[10px] text-text-tertiary">{formatFileSize(att.sizeBytes)}</p>
          <div className="mt-1">{renderDescriptionEditor(att, true)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="attachments-section">
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-tertiary">
          {t('attachments.count', { count: attachments.length + (pinnedItem ? 1 : 0) })}
        </span>
        <div className="flex items-center gap-0.5 rounded-md bg-bg-tertiary p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('list')}
            title={t('attachments.viewList')}
            className={`rounded p-1.5 transition-colors ${
              viewMode === 'list'
                ? 'bg-bg-elevated text-accent-600 shadow-sm'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            <List className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            title={t('attachments.viewGrid')}
            className={`rounded p-1.5 transition-colors ${
              viewMode === 'grid'
                ? 'bg-bg-elevated text-accent-600 shadow-sm'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {pinnedItem}

      {/* Strefa przeciągnij-i-upuść */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="attachments-dropzone"
        className={[
          'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors',
          dragOver
            ? 'border-accent-500 bg-accent-500/5'
            : 'border-border-primary hover:border-accent-500/50 hover:bg-bg-tertiary/40',
        ].join(' ')}
      >
        <Upload className="mb-1.5 h-5 w-5 text-text-muted" aria-hidden="true" />
        <p className="text-sm font-medium text-text-secondary">{t('attachments.dropzone.title')}</p>
        <p className="mt-0.5 text-xs text-text-muted">{t('attachments.dropzone.subtitle')}</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
          className="hidden"
          // Plik wgrywa się od razu — to nie jest „niezapisana zmiana" formularza, w którym
          // sekcja jest osadzona (zabezpieczenie zamknięcia okna).
          data-no-dirty
          data-testid="attachments-file-input"
        />
        {uploadingNames.length > 0 && (
          <div className="mt-2 flex items-center gap-2 text-xs text-text-tertiary">
            <Spinner size="sm" />
            {t('attachments.uploading', { count: uploadingNames.length })}
          </div>
        )}
      </div>

      {listQuery.isLoading && (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      )}

      {listQuery.isError && (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-error-500">
          <AlertCircle className="h-4 w-4" />
          {t('attachments.loadError')}
        </div>
      )}

      {!listQuery.isLoading && !listQuery.isError && attachments.length === 0 && !pinnedItem && (
        <div className="py-4 text-center" data-testid="attachments-empty">
          <Paperclip className="mx-auto mb-2 h-6 w-6 text-text-muted" aria-hidden="true" />
          <p className="text-sm text-text-tertiary">{t('attachments.empty')}</p>
        </div>
      )}

      {attachments.length > 0 && viewMode === 'list' && (
        <ul className="space-y-2" data-testid="attachments-list">
          {attachments.map(renderRow)}
        </ul>
      )}

      {attachments.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="attachments-grid">
          {attachments.map(renderTile)}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('attachments.confirmDelete.title')}
        message={
          pendingDelete
            ? t('attachments.confirmDelete.message', { fileName: pendingDelete.fileName })
            : ''
        }
        variant="danger"
        confirming={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      {/*
        Podgląd — pojedynczy załącznik w `Modal` (title pominięty celowo: panel
        ma WŁASNY toolbar z nazwą/rozmiarem/zoomem/obrotem/pobraniem/pełnym
        ekranem, dublowanie nazwy w headerze Modala byłoby redundantne). Reconciliacja
        z tabelką (Fala B, `DocumentPreviewPanel` powstał PO tym widgecie — miał
        swój własny, prostszy modal z img/iframe): teraz WSPÓLNY viewer, zero
        duplikacji logiki blob-fetch/zoom/obrót/fullscreen.
      */}
      <Modal open={previewTarget !== null} onClose={closePreview} size="xl">
        <DocumentPreviewPanel
          attachments={previewTarget ? [previewTarget] : []}
          activeAttachmentId={previewTarget?.id ?? null}
          onActiveAttachmentChange={() => {}}
          mode="inline"
          showThumbnails={false}
          height="70vh"
        />
      </Modal>
    </div>
  );
}

export default AttachmentsSection;
